<template>
    <modal-dialog ref="modal" :size="7">
        <flex-container v-if="hasValidSubscription">
            <flex-container direction="column" :gap="Size.sm">
                <text-decorator weight="bold" :size="FontSize.xs">
                    Upgrade
                </text-decorator>
                <text-heading>
                    Upgrade jouw {{ brandLabel }}
                </text-heading>
                <text-description>
                    Het upgraden van je {{ brandLabel }} licentie kan niet makkelijker. Of je nu op zoek bent naar een uitgebreider abonnement met meer mogelijkheden of gewoon wat extra's wilt toevoegen aan wat je al hebt, zoals meer gebruikers of contacten, hier regel jij dat met een paar klikken.
                </text-description>
            </flex-container>

            <inline-tabs selected-name="subscription">
                <template #labels>
                    <inline-tab-label name="subscription">
                        Abonnement
                    </inline-tab-label>
                    <inline-tab-label name="addon">
                        Add-ons
                    </inline-tab-label>
                </template>
                <template #panels>
                    <inline-tab-panel name="subscription" with-variable-height>
                        <flex-container>
                            <flex-container :size="2" :gap="Size.sm">
                                <text-heading sub>
                                    Abonnementen
                                </text-heading>
                                <text-description>
                                    Op deze pagina vind je alles wat je moet weten over de beschikbare abonnementen en welke je nu hebt. Als je meer wilt, kun je hier makkelijk naar een uitgebreider pakket overstappen. Wil je juist minder? Neem dan contact op met je accountmanager, die helpt je verder.
                                </text-description>
                            </flex-container>
                            <flex-container :size="8">
                                <subscriptions-form
                                    ref="subscriptionForm"
                                    :current-subscription="currentSubscription"
                                    :plan-collection="planCollection"
                                >
                                    <template #actions>
                                        <button-text @click="modal?.closeOverlay()">
                                            Terug
                                        </button-text>
                                        <button-block
                                            v-tooltip="canChangePlan ? 'Sla je wijzigingen op' : 'Je kunt je abonnement niet aanpassen. Neem contact op met je accountmanager.'"
                                            :disabled="subscriptionForm?.form.plan === null || !canChangePlan"
                                            @loading="saveSubscription($event)"
                                        >
                                            Bevestigen
                                        </button-block>
                                    </template>
                                </subscriptions-form>
                            </flex-container>
                        </flex-container>
                    </inline-tab-panel>
                    <inline-tab-panel name="addon" with-variable-height>
                        <flex-container>
                            <flex-container :size="2" :gap="Size.sm">
                                <text-heading sub>
                                    Add-ons
                                </text-heading>
                                <text-description>
                                    Heb je meer gebruikers, contacten of merken nodig binnen je huidige abonnement? Geen probleem! Op deze pagina kun je gemakkelijk kiezen wat je nodig hebt. Met slechts één klik is alles geregeld en kun je direct verder.
                                </text-description>
                            </flex-container>
                            <flex-container :size="8">
                                <!-- Winck Addons -->
                                <winck-addon-form
                                    v-if="hasWinckBranding()"
                                    ref="exposedWinckAddonForm"
                                    :current-subscription="currentSubscription"
                                    :loaded-additional-costs="loadedAdditionalCosts"
                                    :additional-costs="currentAdditionalCostsForWinck"
                                >
                                    <template #actions>
                                        <button-text @click="modal?.closeOverlay()">
                                            Terug
                                        </button-text>
                                        <button-block
                                            :disabled="loadedAdditionalCosts === currentAdditionalCostsForWinck"
                                            @loading="saveWinckAddons($event)"
                                        >
                                            Bevestigen
                                        </button-block>
                                    </template>
                                </winck-addon-form>

                                <!-- Asknoa Addons -->
                                <ask-noa-addon-form
                                    v-if="!hasWinckBranding()"
                                    ref="exposedAskNoaAddonForm"
                                    :current-subscription="currentSubscription"
                                    :loaded-additional-costs="loadedAdditionalCosts"
                                    :additional-costs="currentAdditionalCostsForAskNoa"
                                >
                                    <template #actions>
                                        <button-text @click="modal?.closeOverlay()">
                                            Terug
                                        </button-text>
                                        <button-block
                                            :disabled="loadedAdditionalCosts === currentAdditionalCostsForAskNoa"
                                            @loading="saveAskNoaAddons($event)"
                                        >
                                            Bevestigen
                                        </button-block>
                                    </template>
                                </ask-noa-addon-form>
                            </flex-container>
                        </flex-container>
                    </inline-tab-panel>
                </template>
            </inline-tabs>
        </flex-container>
        <flex-container
            v-else
            :gap="Size.sm"
            align-content="center"
            align-items="center"
            direction="column"
        >
            <svg-icon :icon="WarningIcon" :color="'orange-500'" />
            <text-heading>
                Er is iets mis gegaan met het inladen van het abonnement
            </text-heading>
            <text-description>
                Neem contact op met je contactpersoon.
            </text-description>
        </flex-container>
    </modal-dialog>
</template>

<script setup lang="ts">
    import { ButtonState, type ExposedOverlay, FontSize, Size } from '@keyvue/one-ui';
    import { computed, onMounted, ref } from 'vue';
    import SubscriptionsForm, { type ExposedSubscriptionsForm } from '@/subscription/subscription/components/SubscriptionsForm.vue';
    import WinckAddonForm, { type ExposedWinckAddonForm } from '@/subscription/subscription/components/WinckAddonForm.vue';
    import AskNoaAddonForm, { type ExposedAskNoaAddonForm } from '@/subscription/subscription/components/AskNoaAddonForm.vue';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import {
        Plan,
        Subscription,
        ChangeSubscriptionPlanCommand,
        ChangeWinckSubscriptionAddonCommand,
        ChangeAskNoaSubscriptionAddonCommand,
    } from '@keyvue/one-api-client';
    import AdditionalCostsCalculator from '@/subscription/subscription/utils/AdditionalCostsCalculator';
    import WarningIcon from '@keyvue/one-material-icons/icons/warning.svg?component';
    import useAppBranding from '@/services/app-branding/composables/useAppBranding';

    const modal = ref<ExposedOverlay>();
    const subscriptionForm = ref<ExposedSubscriptionsForm>();
    const exposedWinckAddonForm = ref<ExposedWinckAddonForm>();
    const exposedAskNoaAddonForm = ref<ExposedAskNoaAddonForm>();

    const { getLazyResourceCollection, currentCompany, client } = useClient();
    const { hasWinckBranding } = useAppBranding();

    const loadedAdditionalCosts = ref(0);
    const planCollection = getLazyResourceCollection(Plan);
    const subscriptionCollection = getLazyResourceCollection(Subscription);
    const loadablePlanSlugs = ref<string[]>([]);

    onMounted(async () => {
        await load();

        modal.value?.addOverlayEventListener('open', load);
    });

    async function load() {
        if (hasWinckBranding()) {
            loadablePlanSlugs.value = [
                'winck-silver',
                'winck-gold',
                'winck-platinum',
            ];
        } else {
            loadablePlanSlugs.value = [
                'asknoa-silver',
                'asknoa-gold',
            ];
        }

        await planCollection.load({
            'slug': loadablePlanSlugs.value,
            'order[price]': 'ASC',
        });

        await subscriptionCollection.load({
            company: currentCompany.value.id,
        });

        if (!hasValidSubscription.value) {
            return;
        }

        if (hasWinckBranding()) {
            loadedAdditionalCosts.value = AdditionalCostsCalculator.calculateForWinck(currentSubscription.value.winckSubscriptionAddon);
        } else {
            loadedAdditionalCosts.value = AdditionalCostsCalculator.calculateForAskNoa(currentSubscription.value.askNoaSubscriptionAddon);
        }

        subscriptionForm.value?.setForm({ plan: null });

        exposedWinckAddonForm.value?.setForm(currentSubscription.value);
        exposedAskNoaAddonForm.value?.setForm(currentSubscription.value);
    }

    async function saveSubscription(event) {
        if (!subscriptionForm.value || !subscriptionForm.value.form.plan) {
            event.button.setState(ButtonState.completed);
            return;
        }

        await client.dispatch(new ChangeSubscriptionPlanCommand(
            currentSubscription.value.id,
            subscriptionForm.value.form.plan.id,
        ));

        await load();

        event.button.setState(ButtonState.completed);
    }

    async function saveWinckAddons(event) {
        if (!exposedWinckAddonForm.value) {
            event.button.setState(ButtonState.completed);
            return;
        }

        await client.dispatch(new ChangeWinckSubscriptionAddonCommand(
            currentSubscription.value.id,
            exposedWinckAddonForm.value.form.users,
            exposedWinckAddonForm.value.form.contacts,
            exposedWinckAddonForm.value.form.brands,
            exposedWinckAddonForm.value.form.premiumFlows,
            exposedWinckAddonForm.value.form.aiEmployees,
        ));

        await load();

        event.button.setState(ButtonState.completed);
    }

    async function saveAskNoaAddons(event) {
        if (!exposedAskNoaAddonForm.value) {
            event.button.setState(ButtonState.completed);
            return;
        }

        await client.dispatch(new ChangeAskNoaSubscriptionAddonCommand(
            currentSubscription.value.id,
            exposedAskNoaAddonForm.value.form.aiEmployees,
        ));

        await load();

        event.button.setState(ButtonState.completed);
    }

    const hasSubscription = computed(() => subscriptionCollection.data.value.length > 0);
    const currentSubscription = computed(() => subscriptionCollection.data.value[0]);

    const currentAdditionalCostsForWinck = computed(() => AdditionalCostsCalculator.calculateForWinck(exposedWinckAddonForm.value?.form ?? null));
    const currentAdditionalCostsForAskNoa = computed(() => AdditionalCostsCalculator.calculateForAskNoa(exposedAskNoaAddonForm.value?.form ?? null));

    const hasWinckSubscription = computed(() => currentSubscription.value.winckSubscriptionAddon !== null);
    const hasAskNoaSubscription = computed(() => currentSubscription.value.askNoaSubscriptionAddon !== null);

    const hasValidSubscription = computed(() => hasSubscription.value && (hasWinckSubscription.value || hasAskNoaSubscription.value));
    const canChangePlan = computed(() => loadablePlanSlugs.value.includes(currentSubscription.value.plan.slug));

    const brandLabel = hasWinckBranding() ? 'Winck' : 'AskNoa';

    defineExpose({
        overlay: modal,
    });
</script>

<style scoped lang="scss">

</style>
